// src/components/SNRGauge.tsx
// SVG arc gauge — faithful port of the Skia-based mobile component

interface SNRGaugeProps {
  pct: number
  status: string
}

export function SNRGauge({ pct, status }: SNRGaugeProps) {
  const size = 220
  const strokeWidth = 14
  const r = (size - strokeWidth) / 2
  const cx = size / 2
  const cy = size / 2
  const startAngle = 135
  const totalAngle = 270
  const endAngle = startAngle + totalAngle

  // Arc path builder (degrees → SVG coords)
  function polarToXY(angleDeg: number): [number, number] {
    const rad = ((angleDeg - 90) * Math.PI) / 180
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)]
  }

  function describeArc(start: number, end: number): string {
    const [sx, sy] = polarToXY(start)
    const [ex, ey] = polarToXY(end)
    const large = end - start > 180 ? 1 : 0
    return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`
  }

  const trackPath = describeArc(startAngle, endAngle)

  // Fill arc: clamp to very small angle so rounded caps render even at 0%
  const fillEndAngle = startAngle + Math.max(0.5, (pct / 100) * totalAngle)
  const fillPath = describeArc(startAngle, fillEndAngle)

  const strokeColor =
    pct >= 75 ? 'var(--signal)' :
    pct >= 50 ? 'var(--amber)' :
    'var(--noise)'

  return (
    <div className="snr-gauge-wrap">
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ overflow: 'visible' }}>
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Track */}
          <path
            d={trackPath}
            fill="none"
            stroke="var(--card-high)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />

          {/* Glow layer */}
          <path
            d={fillPath}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            filter="url(#glow)"
            opacity={0.5}
            style={{ transition: 'd 1s cubic-bezier(0.4,0,0.2,1)' }}
          />

          {/* Fill arc */}
          <path
            d={fillPath}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{ transition: 'd 1s cubic-bezier(0.4,0,0.2,1)' }}
          />
        </svg>

        {/* Center text */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span className="display-lg text-primary">{pct}%</span>
          <span className="label text-muted" style={{ marginTop: 2 }}>{status}</span>
        </div>
      </div>
    </div>
  )
}
